import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ClipboardList, Send, Trash2, Key, Users, Database } from 'lucide-react'
import toast from 'react-hot-toast'
import carteraService from '@/services/carteraService'
import { opcionesAlcanceSiembra, ayudaAlcance } from '@/lib/cd2bSiembra'
import { mensajeDeError } from '@/lib/errores'
import { useConfirmacion } from '@/hooks/useConfirmacion'
import RemapCuentas from '@/components/cd2b/RemapCuentas'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import EntregaAProduccion from './EntregaAProduccion'
import EstadoCatalogoContador from './EstadoCatalogoContador'
import CuentaDueno from './CuentaDueno'
import CobroEmpresa from './CobroEmpresa'
import { fecha, sinEspacioNube } from './formato'

/* ─────────────────────────── Gestión ───────────────────────────
 *
 * Todo lo que sirve para TRABAJAR la empresa vivía en el editor de la COTIZACIÓN. Es decir: en la
 * pantalla de venta. Una vez vendida, el asesor tenía que volver ahí para dictar un acceso o
 * revisar los datos de prueba, y una empresa que llegó sin cotización —registro propio, traspaso,
 * siembra de admin— no llegaba nunca, aunque estuviera en su cartera.
 *
 * El ESTADO decide qué se ofrece, y lo vuelve a decidir el servidor:
 *   · DEMO (en prueba)  → acceso, credenciales por puesto, y datos de prueba (sembrar/deshacer).
 *   · PRODUCCIÓN        → acceso y credenciales. Los datos de prueba NO existen: sembrar sobre la
 *                         contabilidad de un cliente vivo no es una opción que haya que ofrecer
 *                         con cuidado, es una que no debe estar.
 */

function Copiable({ etiqueta, valor }) {
  if (!valor) return null
  return (
    <p className="flex items-center gap-2 flex-wrap">
      <span className="text-neutral-500 dark:text-neutral-400">{etiqueta}:</span>
      <code className="font-mono font-semibold text-neutral-900 dark:text-neutral-50">{valor}</code>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => { navigator.clipboard?.writeText(valor); toast.success(`${etiqueta} copiado`) }}
      >
        Copiar
      </Button>
    </p>
  )
}

export default function Gestion({ ficha, onCambio }) {
  const navigate = useNavigate()
  const esDemo = !!ficha.esDemo
  // Pagada y sin entregar: el espacio en la nube todavía no existe. En vez de dejar que cada botón
  // falle con un toast («no tiene una instancia en la nube configurada»), se dice arriba qué pasa
  // y se ofrece el paso que lo resuelve.
  const sinNube = sinEspacioNube(ficha)
  const pagadaSinEntregar = sinNube && ficha.propuestaOrigen?.estado === 'pagada'
  const motivoSinNube = 'Disponible cuando la empresa esté entregada a producción (todavía no tiene su espacio en la nube).'
  const [acceso, setAcceso] = useState(null)
  const [credenciales, setCredenciales] = useState(null)
  const [cargas, setCargas] = useState(null)
  const [alcance, setAlcance] = useState('operacion')
  const [trabajando, setTrabajando] = useState(false)
  const { confirmar, pedirDatos, dialogo } = useConfirmacion()

  const fallo = (e, porDefecto) => toast.error(mensajeDeError(e, porDefecto))

  const verAcceso = async () => {
    setTrabajando(true)
    try {
      let r = await carteraService.accesoEmpresa(ficha.subscriptionId)
      let d = r?.data || {}
      // La lectura no emite nada: si no hay código vigente, emitirlo es una decisión del asesor
      // (y en una empresa ya en producción, una que hay que confirmar).
      if (!d.negocioCodigo) {
        const seguir = await confirmar(esDemo
          ? {
            titulo: 'Generar código de acceso',
            descripcion: 'Esta empresa no tiene un código de acceso vigente. Se generará uno nuevo.',
            textoConfirmar: 'Generar código',
          }
          : {
            titulo: 'Generar acceso a una empresa EN PRODUCCIÓN',
            descripcion: 'Esta empresa ya está EN PRODUCCIÓN y no tiene un código de acceso vigente. Generar uno da entrada a sus datos reales.',
            textoConfirmar: 'Generar código',
            variante: 'destructiva',
          })
        if (!seguir) { setAcceso(d); return }
        r = await carteraService.emitirAccesoEmpresa(ficha.subscriptionId, { confirmar: !esDemo })
        d = r?.data || {}
      }
      setAcceso(d)
      if (d.negocioCodigo) { try { await navigator.clipboard.writeText(d.negocioCodigo) } catch { /* noop */ } }
      toast.success('Acceso del dueño — código copiado')
    } catch (e) { fallo(e, 'No se pudo obtener el acceso') } finally { setTrabajando(false) }
  }

  const [bandeja, setBandeja] = useState(null)

  /**
   * Bandeja de facturas: la dirección a la que los proveedores le mandan los DTE. Se consulta al
   * pulsar, no al abrir la ficha: es una llamada por empresa y el asesor solo la necesita cuando
   * va a dictarla.
   */
  const verBandeja = async () => {
    setTrabajando(true)
    try {
      const r = await carteraService.bandejaEmpresa(ficha.subscriptionId)
      const d = r?.data || {}
      setBandeja(d)
      if (d.direccion) {
        try { await navigator.clipboard.writeText(d.direccion) } catch { /* noop */ }
        toast.success('Bandeja de facturas — dirección copiada')
      }
    } catch (e) { fallo(e, 'No se pudo leer la bandeja de facturas') } finally { setTrabajando(false) }
  }

  /**
   * Da la bandeja. Una empresa de MUESTRA no la recibe sola —no le llegan facturas de nadie—, así
   * que para esas se pide un motivo y se manda `forzar`; el motivo queda grabado en el buzón.
   */
  const darBandeja = async () => {
    let motivo = ''
    if (esDemo) {
      // El diálogo no deja enviar el motivo vacío; cancelar devuelve null y no se hace nada.
      const datos = await pedirDatos({
        titulo: 'Dar bandeja a una empresa de muestra',
        descripcion: 'Esta empresa es de MUESTRA y por eso no recibe bandeja. ¿Para qué la necesita? Queda registrado.',
        campos: [{ clave: 'motivo', etiqueta: 'Motivo' }],
        textoConfirmar: 'Dar bandeja',
      })
      if (!datos) return
      motivo = datos.motivo.trim()
    }
    setTrabajando(true)
    try {
      const r = await carteraService.asignarBandejaEmpresa(ficha.subscriptionId, { forzar: esDemo, motivo: motivo || '' })
      const d = r?.data || {}
      if (d.estado === 'alta') toast.success(d.direccion ? `Bandeja lista: ${d.direccion}` : d.mensaje)
      else toast(d.mensaje || 'No se dio de alta la bandeja', { duration: 9000 })
      await verBandeja()
    } catch (e) { fallo(e, 'No se pudo dar la bandeja de facturas') } finally { setTrabajando(false) }
  }

  const verCredenciales = async () => {
    setTrabajando(true)
    try {
      const r = await carteraService.credencialesEmpresa(ficha.subscriptionId)
      const lista = r?.data?.credenciales || []
      setCredenciales(lista)
      if (!lista.length) toast('Esta empresa todavía no tiene usuarios sembrados.')
    } catch (e) { fallo(e, 'No se pudieron leer los accesos') } finally { setTrabajando(false) }
  }

  const verCargas = async () => {
    setTrabajando(true)
    try {
      const r = await carteraService.cargasEmpresa(ficha.subscriptionId)
      setCargas(r?.data || { cargas: [] })
    } catch (e) { fallo(e, 'No se pudieron leer las cargas') } finally { setTrabajando(false) }
  }

  const sembrar = async () => {
    setTrabajando(true)
    try {
      await carteraService.sembrarEnEmpresa(ficha.subscriptionId, { giro: ficha.giro, alcance })
      toast.success('Siembra lanzada. Tarda varios minutos; revisa las cargas.')
      verCargas()
    } catch (e) { fallo(e, 'No se pudo sembrar') } finally { setTrabajando(false) }
  }

  /**
   * VACIAR la empresa: dejarla sin operación para entregarla limpia.
   *
   * Es lo que hace falta al final de una implementación —y lo que la reversión por carga no
   * alcanza—: lo que el asesor tecleó a mano o trajo del sistema anterior no nació marcado, así
   * que ninguna carga se lo lleva. Se cuenta antes de borrar; esto es destructivo y no se
   * confirma a ciegas.
   */
  const vaciar = async () => {
    setTrabajando(true)
    try {
      const previa = await carteraService.vaciarEmpresa(ficha.subscriptionId, { previsualizar: true })
      const d = previa?.data || {}
      // `porTabla` es un ARRAY de { tabla, filas } — no un mapa.
      const detalle = (d.porTabla || []).map((t) => `· ${t.tabla}: ${t.filas}`).join('\n')
      const total = d.total ?? 0
      if (!total) { toast('La empresa ya está vacía: se puede entregar tal cual.'); return }
      const seguir = await confirmar({
        titulo: `Vaciar «${ficha.empresaNombre}»`,
        descripcion: `Se borrarán ${total} fila(s) de operación.\n${detalle}\n\nEsto no se puede deshacer.`,
        textoConfirmar: 'Vaciar la empresa',
        variante: 'destructiva',
      })
      if (!seguir) return
      await carteraService.vaciarEmpresa(ficha.subscriptionId)
      toast.success('Empresa vaciada. Queda lista para entregar.')
      verCargas()
      onCambio?.()
    } catch (e) {
      if (e?.response?.data?.code === 'NO_ES_DEMO') {
        toast.error('Esta empresa ya está en producción: su operación es real y no se vacía.', { duration: 10000 })
      } else { fallo(e, 'No se pudo vaciar') }
    } finally { setTrabajando(false) }
  }

  /**
   * BORRAR la empresa entera: tenant + toda su huella en Mongo, para siempre. A diferencia de
   * "vaciar" (arriba, que solo limpia datos), esto destruye el negocio de verdad — por eso solo
   * se ofrece mientras siga en modo demo (el backend lo exige igual, esto es para no dejar
   * clicar un botón que va a fallar). Se previsualiza SIEMPRE antes: si hay algo que lo bloquea
   * (DTE sellado, ya cobró, comisión pagada), se avisa sin intentar el borrado.
   */
  const borrar = async () => {
    setTrabajando(true)
    try {
      const previa = await carteraService.previsualizarEliminarEmpresa(ficha.subscriptionId)
      const d = previa?.data || {}
      if (d.permitido === false) {
        toast.error(d.bloqueos?.[0]?.mensaje || 'Esta empresa no se puede borrar.', { duration: 12000 })
        return
      }
      const inv = d.seBorraria || {}
      const detalle = [
        inv.addendas ? `${inv.addendas} addenda(s)` : null,
        inv.cotizaciones ? `${inv.cotizaciones} cotización(es)` : null,
        inv.comisionesMonto ? `$${inv.comisionesMonto} en comisión(es) del asesor` : null,
      ].filter(Boolean).join(', ')
      // Escribir el nombre EXACTO (sin distinguir mayúsculas) habilita el botón: el diálogo no deja
      // confirmar con un nombre que no coincide, así que no hay «no se borró nada» que avisar.
      const nombre = (ficha.empresaNombre || '').trim()
      const seguir = await confirmar({
        titulo: 'Borrar la empresa para siempre',
        descripcion:
          `Esto borra «${ficha.empresaNombre}» PARA SIEMPRE: su empresa y todo su historial` +
          `${detalle ? ` (${detalle})` : ''}. No se puede deshacer.`,
        textoConfirmar: 'Borrar empresa',
        variante: 'destructiva',
        confirmacionEscrita: nombre,
      })
      if (!seguir) return
      await carteraService.eliminarEmpresa(ficha.subscriptionId, nombre)
      toast.success('Empresa borrada.')
      onCambio?.()
    } catch (e) {
      if (e?.response?.data?.code === 'NO_ES_DEMO') {
        toast.error('Esta empresa ya está en producción: no se puede borrar desde el portal.', { duration: 10000 })
      } else { fallo(e, 'No se pudo borrar') }
    } finally { setTrabajando(false) }
  }

  const revertir = async (carga) => {
    // Se cuenta antes de borrar: deshacer una carga que el cliente ya usó se rechaza con 409 y el
    // detalle de qué la bloquea, y eso hay que verlo antes de confirmar, no después.
    setTrabajando(true)
    try {
      const previa = await carteraService.revertirCargaEmpresa(ficha.subscriptionId, carga.id, { previsualizar: true })
      const n = previa?.data?.filas ?? carga.filas ?? 0
      const seguir = await confirmar({
        titulo: 'Deshacer la carga',
        descripcion: `Se quitarán ${n} fila(s) de «${carga.descripcion || carga.tipo || 'esta carga'}».`,
        textoConfirmar: 'Deshacer',
        variante: 'destructiva',
      })
      if (!seguir) return
      await carteraService.revertirCargaEmpresa(ficha.subscriptionId, carga.id)
      toast.success('Carga deshecha')
      verCargas()
      onCambio?.()
    } catch (e) {
      const d = e?.response?.data
      if (d?.code === 'CARGA_EN_USO') {
        toast.error(`No se puede deshacer: el cliente ya la usó. ${(d.bloqueos || []).slice(0, 3).join(' · ')}`, { duration: 12000 })
      } else { fallo(e, 'No se pudo deshacer') }
    } finally { setTrabajando(false) }
  }

  return (
    <div className="space-y-4">
      {dialogo}
      {sinNube && (
        <div role="status" className="rounded-lg border border-warning-300 dark:border-warning-700 bg-warning-50 dark:bg-warning-900/20 p-4 flex items-start gap-2.5">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning-700 dark:text-warning-300" />
          <div className="text-body-sm text-warning-800 dark:text-warning-300 space-y-1">
            <p className="font-semibold">
              {pagadaSinEntregar ? 'Pagada — falta entregarla a producción' : 'Esta empresa todavía no tiene su espacio en la nube'}
            </p>
            <p>
              El espacio en la nube se crea al <span className="font-medium">entregar a producción</span>, no al pagar.
              Hasta entonces no hay acceso del dueño ni accesos por puesto.
              {ficha.propuestaOrigen?.id ? ' Revisa los requisitos y entrégala en la tarjeta «Entregar a producción» de abajo.' : ' Pide a un administrador que la aprovisione.'}
            </p>
          </div>
        </div>
      )}
      <CobroEmpresa subscriptionId={ficha.subscriptionId} onCambio={onCambio} />
      <Card className="p-4 sm:p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-neutral-900 dark:text-neutral-50">Acceso del dueño</h3>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              Lo que le dictas para que entre desde el escritorio o desde el teléfono.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={verAcceso} disabled={trabajando || sinNube} title={sinNube ? motivoSinNube : undefined}>
              <Key size={15} className="mr-1.5" /> Ver acceso del dueño
            </Button>
            <Button variant="outline" onClick={verCredenciales} disabled={trabajando || sinNube} title={sinNube ? motivoSinNube : undefined}>
              <Users size={15} className="mr-1.5" /> Accesos por puesto
            </Button>
          </div>
        </div>

        {/* La cuenta de LOGIN del dueño (correo) va siempre, incluso antes de entregar: la cuenta
            existe desde la cotización, y un correo mal escrito se corrige mejor antes de que entre. */}
        <CuentaDueno subscriptionId={ficha.subscriptionId} confirmar={confirmar} pedirDatos={pedirDatos} />

        {acceso && (
          <div className="rounded-lg bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-800 p-4 space-y-3 text-body-sm">
            <div>
              <p className="font-medium text-neutral-900 dark:text-neutral-50">App de escritorio (pestaña «Cuenta»)</p>
              <Copiable etiqueta="Correo" valor={acceso.escritorio?.correo} />
              <Copiable etiqueta="Contraseña temporal" valor={acceso.escritorio?.passwordTemp} />
              {acceso.escritorio?.cuentaExistente && !acceso.escritorio?.passwordTemp && (
                <p className="text-caption text-neutral-500 dark:text-neutral-400">
                  {acceso.avisoSinCotizacion
                    || 'Esta cuenta ya se usó: la contraseña es del dueño y no la tenemos. Si la perdió, que use «Olvidé mi contraseña».'}
                </p>
              )}
              {acceso.escritorio?.passwordCambiadaPorDueno && (
                <p className="text-caption text-warning-700 dark:text-warning-300">
                  El dueño ya cambió su contraseña: la temporal de arriba ya no sirve.
                </p>
              )}
            </div>
            <div>
              <p className="font-medium text-neutral-900 dark:text-neutral-50">App móvil (código de empresa)</p>
              <Copiable etiqueta="Código de empresa" valor={acceso.negocioCodigo} />
              <Copiable etiqueta="Usuario" valor={acceso.usuarioAdmin} />
              <Copiable etiqueta="PIN" valor={acceso.pinAdmin} />
            </div>
          </div>
        )}

        {credenciales && (
          <div className="rounded-lg bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-800 p-4 text-body-sm">
            {credenciales.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400">Todavía no hay usuarios sembrados en esta empresa.</p>
            ) : (
              <ul className="space-y-1.5">
                {credenciales.map((c) => (
                  <li key={`${c.nombre}-${c.rol}`} className="flex flex-wrap items-center justify-between gap-2">
                    <span>
                      <span className="font-medium">{c.nombre}</span>
                      <span className="text-neutral-500 dark:text-neutral-400"> — {c.puesto || c.rol}</span>
                    </span>
                    {c.pin ? (
                      <code className="font-mono font-semibold text-neutral-900 dark:text-neutral-50">PIN {c.pin}</code>
                    ) : (
                      <span className="text-caption text-neutral-500 dark:text-neutral-400">PIN propio — «Ver acceso del dueño»</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>

      <Card className="p-4 sm:p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-neutral-900 dark:text-neutral-50">Bandeja de facturas</h3>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              La dirección a la que sus proveedores le mandan los DTE. Lo que llega ahí se procesa
              solo: se descarta la basura y cada comprobante se propone como compra.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={verBandeja} disabled={trabajando}>
              <ClipboardList size={15} className="mr-1.5" /> Ver bandeja
            </Button>
            {bandeja && !bandeja.activa && (
              <Button variant="outline" onClick={darBandeja} disabled={trabajando}>
                <Send size={15} className="mr-1.5" /> Dar bandeja de facturas
              </Button>
            )}
          </div>
        </div>

        {bandeja && (
          <div className="rounded-lg bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-800 p-4 space-y-2 text-body-sm">
            {bandeja.direccion ? (
              <Copiable etiqueta="Dirección" valor={bandeja.direccion} />
            ) : (
              <p className="text-neutral-500 dark:text-neutral-400">
                {bandeja.esDemo
                  ? 'Esta empresa es de MUESTRA: no recibe bandeja sola, porque no le llegan facturas de nadie. Si la necesita para enseñar el circuito, dásela con el botón (pedirá un motivo).'
                  : 'Todavía no tiene bandeja. Con el botón se la das; si el sistema se niega, te dirá exactamente por qué.'}
              </p>
            )}
            {bandeja.pendiente && (
              <p className="text-caption text-warning-700 dark:text-warning-300">
                Dirección reservada pero aún no creada en el servidor de correo
                {bandeja.motivoPendiente ? ` (${bandeja.motivoPendiente})` : ''}: se reintenta sola.
              </p>
            )}
            {bandeja.altaForzada && (
              <p className="text-caption text-neutral-500 dark:text-neutral-400">
                Bandeja dada a mano por ser empresa de muestra
                {bandeja.motivoAltaForzada ? `: «${bandeja.motivoAltaForzada}»` : ''}.
              </p>
            )}
          </div>
        )}
      </Card>

      {esDemo ? (
        <Card className="p-4 sm:p-5 space-y-3">
          <div>
            <h3 className="font-semibold text-neutral-900 dark:text-neutral-50">Datos de prueba</h3>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              Todo lo que siembres queda agrupado en cargas reversibles. Antes de pasar la empresa a
              producción hay que decidir qué se hace con ellas.
            </p>
            {/* El asesor tiene que saber que volver a sembrar es SEGURO. Cada bloque
                comprueba lo suyo antes de escribir y el motor de 12 meses se salta cuando ya
                hay historia, así que una segunda pasada sólo añade lo que faltaba — que es
                cómo se completan las demos entregadas antes de una función nueva. Sin decirlo,
                nadie pulsa el botón dos veces por miedo a duplicar la empresa. */}
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              Puedes volver a sembrar sobre una empresa que ya enseñaste: <strong>completa lo que
              falte sin duplicar nada</strong>. Es la forma de que una demo entregada hace meses
              reciba los datos de las funciones nuevas.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1 basis-full sm:basis-64">
              <p className="text-caption text-neutral-500 dark:text-neutral-400 mb-1">Qué sembrar</p>
              <Select value={alcance} onValueChange={setAlcance}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {opcionesAlcanceSiembra(ficha.giro).map((o) => (
                    <SelectItem key={o.valor} value={o.valor}>{o.etiqueta}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
                {ayudaAlcance(ficha.giro, alcance)}
              </p>
            </div>
            <Button variant="outline" onClick={verCargas} disabled={trabajando}>Ver cargas</Button>
            <Button onClick={sembrar} disabled={trabajando || !ficha.giro}>Sembrar datos de ejemplo</Button>
          </div>
          {/* Deshacer carga por carga no basta para entregar limpio: lo que el asesor tecleó a
              mano o trajo del sistema anterior no nació marcado, así que ninguna carga se lo
              lleva. Esto es la salida de «déjala como recién creada». */}
          <div className="flex items-center justify-between gap-3 flex-wrap border-t border-neutral-100 dark:border-neutral-800 pt-3">
            <p className="text-caption text-neutral-500 dark:text-neutral-400 min-w-0 flex-1 basis-full sm:basis-60">
              ¿Terminaste de enseñarla? Vaciarla la deja sin operación —también lo que tecleaste a
              mano y lo que importaste—, lista para que el dueño empiece de cero.
            </p>
            <Button variant="outline" onClick={vaciar} disabled={trabajando}>
              <Trash2 size={15} className="mr-1.5" /> Vaciar la empresa
            </Button>
          </div>
          {!ficha.giro && (
            <p className="text-caption text-warning-700 dark:text-warning-300">
              Esta empresa no tiene giro registrado: sin él no se sabe qué plantilla sembrar.
            </p>
          )}

          {cargas && (
            cargas.cargas?.length ? (
              <div className="space-y-2 pt-1">
                {cargas.cargas.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 dark:border-neutral-800 pb-2">
                    <div className="min-w-0">
                      <p className="text-body-sm text-neutral-800 dark:text-neutral-200">{c.descripcion || c.tipo || 'Carga'}</p>
                      <p className="text-caption text-neutral-500 dark:text-neutral-400">
                        {fecha(c.creadaEn || c.createdAt)} · {Number(c.filas) || 0} fila(s) · {c.estado}
                      </p>
                    </div>
                    {c.estado === 'activo' && (
                      <Button variant="ghost" size="sm" onClick={() => revertir(c)} disabled={trabajando}>
                        <Trash2 size={14} className="mr-1.5" /> Deshacer
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-caption text-neutral-500 dark:text-neutral-400 pt-1">
                Sin cargas. La empresa está limpia: se puede entregar tal cual.
              </p>
            )
          )}
        </Card>
      ) : (
        <Card className="p-4 sm:p-5">
          <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 mb-1">Datos de prueba</h3>
          <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
            Esta empresa ya está en producción. Los datos de prueba solo existen mientras es una
            demo — sembrar sobre la operación real de un cliente le ensuciaría la contabilidad.
          </p>
        </Card>
      )}

      {/* La ENTREGA, justo después de los datos de prueba y antes de borrar: es el orden en que
          ocurren las cosas. Solo mientras sea una prueba — una empresa ya entregada no se
          vuelve a entregar, y el propio chequeo lo dice si alguien llega aquí de rebote. */}
      {/* También cuando se pagó SIN demo: esa empresa no es «de prueba», pero tampoco existe aún
          en la nube, y la entrega es lo que crea su espacio. Antes la tarjeta no salía y el asesor
          no tenía desde la ficha ningún camino para terminarla. */}
      {(esDemo || sinNube) && <EntregaAProduccion ficha={ficha} onCambio={onCambio} sinDemo={!esDemo} />}

      {/* Borrar la empresa ENTERA, no solo sus datos. Siempre visible (a diferencia de "Datos de
          prueba", que desaparece en producción) para que quede claro que la salida existe pero
          se apagó — no que nunca existió. */}
      <Card className="p-4 sm:p-5 space-y-2 border-danger-200 dark:border-danger-800">
        <div className="flex items-start gap-2">
          <AlertTriangle size={18} className="text-danger-700 dark:text-danger-300 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-semibold text-neutral-900 dark:text-neutral-50">Borrar empresa</h3>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              {esDemo
                ? 'Destruye el tenant y todo su historial. Solo tiene sentido para una demo que no va a ningún lado — irreversible.'
                : 'Ya pasó a producción: esto ya no se puede borrar desde el portal. Si hace falta, pide soporte.'}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={borrar} disabled={trabajando || !esDemo} title={!esDemo ? 'Ya pasó a producción; esto ya no se puede borrar desde el portal.' : undefined}>
          <Trash2 size={15} className="mr-1.5" /> Borrar empresa
        </Button>
      </Card>

      {/* Reasignar el catálogo NO es un dato de prueba: es lo contrario. Los datos de prueba
          sólo existen mientras la empresa es demo; esto es justo para la empresa REAL, la que
          ya lleva meses facturando y cuyo contador quiere su propio plan de cuentas. */}
      <EstadoCatalogoContador ficha={ficha} />

      <RemapCuentas subId={ficha.subscriptionId} />

      <Card className="p-4 sm:p-5">
        <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 mb-1">Su sistema anterior</h3>
        {ficha.propuestaOrigen ? (
          <>
            <p className="text-body-sm text-neutral-600 dark:text-neutral-400 mb-3">
              Trae su catálogo, su cartera y sus saldos antes de entregarle la empresa.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/consultor/cotizaciones/${ficha.propuestaOrigen.id}/migracion`)}
              >
                <Database size={15} className="mr-1.5" /> Migración de datos
              </Button>
              <Button variant="ghost" onClick={() => navigate(`/consultor/cotizaciones/${ficha.propuestaOrigen.id}`)}>
                Abrir su cotización
              </Button>
            </div>
          </>
        ) : (
          // Sin cotización de origen no hay proyecto de migración al que entrar, y ofrecer el botón
          // sería mandar al asesor a una pantalla que no puede resolver nada.
          <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
            Esta empresa no nació de una cotización (se registró por su cuenta o llegó por traspaso),
            así que no tiene un proyecto de migración asociado. Para traerle datos de su sistema
            anterior, ábrele una cotización.
          </p>
        )}
      </Card>
    </div>
  )
}
